// TimeSelect — sélecteur d'heure PARTAGÉ, format 24h, DEUX listes distinctes : Heure (00–23) et
// Minutes (00–55, pas de 5). Remplace le natif <input type="time">.
//
// • Valeur émise = chaîne "HH:MM" (heure locale, SANS fuseau) quand HEURE ET MINUTES sont choisies.
// • BUG CORRIGÉ : avant, choisir l'heure AVANT les minutes émettait "" (condition `h && m`) → la
//   valeur parent repassait à vide → le <select> réaffichait « HH » (heure « qui ne reste pas »).
//   Désormais le composant garde un ÉTAT LOCAL (h, m) : la sélection partielle persiste à l'écran,
//   et on remonte "HH:MM" au parent dès que les deux sont là (et "" tant qu'elles ne le sont pas,
//   pour que la validation « requis » fonctionne correctement). L'état local reste synchronisé si
//   le parent impose une valeur (édition d'un trajet existant, réutilisation…).
import React, { useState, useEffect, useMemo } from "react";
import "./TimeSelect.css";

const pad2 = (n) => String(n).padStart(2, "0");

// Parse "HH:MM" | "HH:MM:SS" | "" → { h: "HH"|"", m: "MM"|"" }.
const parse = (value) => {
  const s = String(value || "").trim();
  const mt = s.match(/^(\d{1,2}):(\d{1,2})/);
  if (!mt) return { h: "", m: "" };
  const h = Math.min(23, parseInt(mt[1], 10));
  const mi = Math.min(59, parseInt(mt[2], 10));
  return { h: pad2(h), m: pad2(mi) };
};

const HOURS = Array.from({ length: 24 }, (_, h) => pad2(h));
const MIN_STEP5 = Array.from({ length: 12 }, (_, i) => pad2(i * 5)); // 00,05,…,55

const TimeSelect = ({ label, value, onChange, required, disabled, name }) => {
  // État LOCAL de chaque liste — indépendant de la valeur parent tant que la saisie est partielle.
  const parsed = parse(value);
  const [h, setH] = useState(parsed.h);
  const [m, setM] = useState(parsed.m);

  // Resynchronise si le parent CHANGE la valeur de l'extérieur (édition, reset, réutilisation).
  // On ne réinitialise pas sur chaque render : seulement quand la valeur entrante diffère de l'état.
  useEffect(() => {
    const p = parse(value);
    // Si le parent fournit une valeur complète différente, on l'adopte.
    if (p.h !== h || p.m !== m) {
      // Mais on NE REMET PAS à vide une saisie partielle en cours si le parent est vide
      // (value "" alors qu'on vient juste de choisir l'heure) → on ne touche que si p est non vide
      // ou si l'état local était déjà vide.
      if (value) { setH(p.h); setM(p.m); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Remonte "HH:MM" au parent. UX : choisir l'HEURE complète les minutes à "00" par défaut
  // (08 → 08:00) → une heure pile est immédiatement valide sans avoir à toucher les minutes.
  // Si on vide l'heure, on remonte "" (la validation « requis » se déclenche normalement).
  const pushUp = (nh, nm) => {
    if (!onChange) return;
    if (nh && !nm) { onChange(`${nh}:00`); return; }     // heure seule → minutes = 00
    onChange(nh && nm ? `${nh}:${nm}` : "");
  };

  const onHour = (e) => {
    const nh = e.target.value;
    setH(nh);
    // Si aucune minute n'est encore choisie, on affiche "00" pour refléter la valeur émise.
    const nm = (nh && !m) ? "00" : m;
    if (nh && !m) setM("00");
    pushUp(nh, nm);
  };
  const onMin  = (e) => { const nm = e.target.value; setM(nm); pushUp(h, nm); };

  // La valeur courante des minutes peut être hors pas de 5 (donnée existante) → on l'ajoute.
  const minuteOptions = useMemo(() => {
    const set = new Set(MIN_STEP5);
    if (m) set.add(m);
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [m]);

  return (
    <div className="timeselect" data-name={name}>
      {label && <label className="timeselect__label">{label}{required && <span className="timeselect__req"> *</span>}</label>}
      <div className="timeselect__controls">
        <select className="timeselect__select" aria-label="Heure" value={h} disabled={disabled} onChange={onHour}>
          <option value="">HH</option>
          {HOURS.map((hh) => <option key={hh} value={hh}>{hh}</option>)}
        </select>
        <span className="timeselect__sep">:</span>
        <select className="timeselect__select" aria-label="Minutes" value={m} disabled={disabled} onChange={onMin}>
          <option value="">MM</option>
          {minuteOptions.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
        </select>
      </div>
    </div>
  );
};

// Exportés pour les tests (logique pure, sans Date → indépendante du fuseau).
export { parse as __parseTime, pad2 as __pad2 };
export default TimeSelect;
